/**
 * MBA Lab — pure state/markdown model for the team charter builder. No DOM,
 * storage, or analytics dependencies - team-charter.js owns all of that and
 * consumes getDocumentModel()/buildMarkdown() from here so the live preview,
 * the .md download, the copy button, and print all come from one source and
 * can never drift apart from each other.
 *
 * Every user-controlled string that flows through here is untrusted: names,
 * goals, and free-text notes can contain anything. This module never builds
 * HTML - team-charter.js is responsible for rendering the document model via
 * textContent/DOM construction, never innerHTML, for exactly that reason.
 * buildMarkdown() escapes markdown-structural characters so pasted/typed
 * content can't reshape the document (a team name of "# Fake Heading" stays
 * a literal string, not a heading) and can never carry raw HTML through to a
 * markdown-to-HTML render.
 */

import {
  AMBITION_KEYS,
  ROTATION_KEYS,
  CADENCE_KEYS,
  CHANNEL_KEYS,
  SLA_KEYS,
  BUFFER_KEYS,
  DECISION_METHOD_KEYS,
  RETRO_KEYS,
  WORKLOAD_SIGNAL_KEYS,
  AMBITION_TEXT,
  ROTATION_TEXT,
  CADENCE_TEXT,
  CHANNEL_TEXT,
  SLA_TEXT,
  BUFFER_TEXT,
  DECISION_METHOD_TEXT,
  RETRO_TEXT,
  WORKLOAD_SIGNAL_TEXT,
} from './charter-presets.js';

export const SCHEMA_NAME = 'mbalab.team-charter';
export const SCHEMA_VERSION = 2;
export const TOTAL_SECTIONS = 9;

export const LIMITS = Object.freeze({
  teamName: 120,
  course: 120,
  term: 120,
  memberName: 80,
  learningGoal: 500,
  sectionText: 2000,
  maxMembers: 8,
  maxSerializedBytes: 100 * 1024,
});

export function makeMemberId() {
  return 'm_' + Math.random().toString(36).slice(2, 10);
}

export function createDefaultState() {
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: null,
    team: { name: '', course: '', term: '' },
    members: [],
    goals: { ambition: null, memberGoals: {} },
    roles: { coordinator: null, integrator: null, qualityBar: null, liaison: null, rotation: 'fixed' },
    meetings: { cadence: 'weekly', norms: { agenda24h: true, hardStop: true, graceFive: true }, customNote: '' },
    comms: { channel: 'group-chat', noDms: true, sla: '24h-weekdays', quietHours: '', recruitingNote: true, workloadSignal: 'say-it-early', customNote: '' },
    quality: { doneMeans: { proofread: true, rubric: true, earlySubmit: true }, buffer: '24h', customNote: '' },
    decisions: { method: 'consensus-24h', wholeTeamItems: '', customNote: '' },
    accountability: { threeStrikes: true, retroCadence: 'per-deliverable', peerEvalNote: true, repairCommitment: true, customNote: '' },
    commitment: { statement: '', revisitDate: '', signaturesEnabled: true },
  };
}

function clampText(value, maxLen) {
  return typeof value === 'string' ? value.slice(0, maxLen) : '';
}

/** For fields the document model renders as a single line/bullet (team name,
 * course, term, member name) - a raw newline would otherwise let typed text
 * visually detach from its container (e.g. a member's name splitting into its
 * own orphaned paragraph). Collapses instead of stripping so the words on
 * either side of a pasted line break stay readable and separated. */
function clampSingleLineText(value, maxLen) {
  if (typeof value !== 'string') return '';
  return value.replace(/\s*[\r\n]+\s*/g, ' ').trim().slice(0, maxLen);
}

function oneOf(value, allowed, fallback) {
  return allowed.includes(value) ? value : fallback;
}

function sanitizeMembers(rawMembers) {
  if (!Array.isArray(rawMembers)) return [];
  const seen = new Set();
  const result = [];
  for (const m of rawMembers) {
    if (result.length >= LIMITS.maxMembers) break;
    if (!m || typeof m !== 'object') continue;
    const validId = typeof m.id === 'string' && /^m_[a-z0-9]+$/i.test(m.id) && !seen.has(m.id);
    const id = validId ? m.id : makeMemberId();
    seen.add(id);
    result.push({ id, name: clampSingleLineText(m.name, LIMITS.memberName) });
  }
  return result;
}

function sanitizeGoals(raw, memberIds) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const memberGoals = {};
  if (r.memberGoals && typeof r.memberGoals === 'object') {
    for (const [id, text] of Object.entries(r.memberGoals)) {
      if (!memberIds.has(id)) continue;
      memberGoals[id] = clampText(text, LIMITS.learningGoal);
    }
  }
  return { ambition: oneOf(r.ambition, AMBITION_KEYS, null), memberGoals };
}

function sanitizeRoles(raw, memberIds) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const pickMember = (v) => (typeof v === 'string' && memberIds.has(v) ? v : null);
  return {
    coordinator: pickMember(r.coordinator),
    integrator: pickMember(r.integrator),
    qualityBar: pickMember(r.qualityBar),
    liaison: pickMember(r.liaison),
    rotation: oneOf(r.rotation, ROTATION_KEYS, 'fixed'),
  };
}

function sanitizeMeetings(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const norms = r.norms && typeof r.norms === 'object' ? r.norms : {};
  return {
    cadence: oneOf(r.cadence, CADENCE_KEYS, 'weekly'),
    norms: {
      agenda24h: norms.agenda24h !== false,
      hardStop: norms.hardStop !== false,
      graceFive: norms.graceFive !== false,
    },
    customNote: clampText(r.customNote, LIMITS.sectionText),
  };
}

function sanitizeComms(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    channel: oneOf(r.channel, CHANNEL_KEYS, 'group-chat'),
    noDms: r.noDms !== false,
    sla: oneOf(r.sla, SLA_KEYS, '24h-weekdays'),
    quietHours: clampSingleLineText(r.quietHours, 120),
    recruitingNote: r.recruitingNote !== false,
    workloadSignal: oneOf(r.workloadSignal, WORKLOAD_SIGNAL_KEYS, 'say-it-early'),
    customNote: clampText(r.customNote, LIMITS.sectionText),
  };
}

function sanitizeQuality(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const doneMeans = r.doneMeans && typeof r.doneMeans === 'object' ? r.doneMeans : {};
  return {
    doneMeans: {
      proofread: doneMeans.proofread !== false,
      rubric: doneMeans.rubric !== false,
      earlySubmit: doneMeans.earlySubmit !== false,
    },
    buffer: oneOf(r.buffer, BUFFER_KEYS, '24h'),
    customNote: clampText(r.customNote, LIMITS.sectionText),
  };
}

function sanitizeDecisions(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    method: oneOf(r.method, DECISION_METHOD_KEYS, 'consensus-24h'),
    wholeTeamItems: clampText(r.wholeTeamItems, LIMITS.sectionText),
    customNote: clampText(r.customNote, LIMITS.sectionText),
  };
}

function sanitizeAccountability(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    threeStrikes: r.threeStrikes !== false,
    retroCadence: oneOf(r.retroCadence, RETRO_KEYS, 'per-deliverable'),
    peerEvalNote: r.peerEvalNote !== false,
    repairCommitment: r.repairCommitment !== false,
    customNote: clampText(r.customNote, LIMITS.sectionText),
  };
}

function sanitizeCommitment(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const validDate = typeof r.revisitDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r.revisitDate);
  return {
    statement: clampText(r.statement, LIMITS.sectionText),
    revisitDate: validDate ? r.revisitDate : '',
    signaturesEnabled: r.signaturesEnabled !== false,
  };
}

/**
 * Normalizes/clamps a raw (possibly attacker- or corruption-shaped) object
 * into a fully valid state: missing keys merge over defaults, unknown keys
 * are dropped, out-of-range values clamp or reject to a safe default. Also
 * where the member-removal cascade actually lives - anything referencing a
 * member ID that isn't in the (already-clamped) member list is dropped here,
 * regardless of whether that dangling reference came from removeMember()
 * upstream or from hand-edited/corrupted storage.
 */
export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const team = r.team && typeof r.team === 'object' ? r.team : {};
  const members = sanitizeMembers(r.members);
  const memberIds = new Set(members.map((m) => m.id));

  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    team: {
      name: clampSingleLineText(team.name, LIMITS.teamName),
      course: clampSingleLineText(team.course, LIMITS.course),
      term: clampSingleLineText(team.term, LIMITS.term),
    },
    members,
    goals: sanitizeGoals(r.goals, memberIds),
    roles: sanitizeRoles(r.roles, memberIds),
    meetings: sanitizeMeetings(r.meetings),
    comms: sanitizeComms(r.comms),
    quality: sanitizeQuality(r.quality),
    decisions: sanitizeDecisions(r.decisions),
    accountability: sanitizeAccountability(r.accountability),
    commitment: sanitizeCommitment(r.commitment),
  };
}

/**
 * 0->1 never shipped for real (see its own note) - proved the mechanism
 * works before it was needed. 1->2 is the first real migration: the
 * Leadership Lab gap-check added `comms.workloadSignal` and
 * `accountability.repairCommitment`. sanitizeState's own missing-key
 * defaulting would handle old data fine even without this entry, but an
 * explicit migration keeps the schema's evolution documented and testable
 * rather than relying on that defaulting silently.
 */
const MIGRATIONS = {
  0: (state) => ({ ...state, version: 1 }),
  1: (state) => ({
    ...state,
    version: 2,
    comms: { ...state.comms, workloadSignal: state.comms?.workloadSignal ?? 'say-it-early' },
    accountability: { ...state.accountability, repairCommitment: state.accountability?.repairCommitment ?? true },
  }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/**
 * Restoration entry point. Accepts either a raw JSON string (as read straight
 * from localStorage) or an already-parsed object (as core.js's store().load()
 * returns, since it does its own JSON.parse). Never throws - every failure
 * mode returns a status instead so the caller can offer "Start fresh" with
 * the raw content still available to copy/download, rather than crashing the
 * builder. `raw` is always a re-serializable string when there's anything to
 * recover, regardless of which input form was passed in - a caller getting an
 * already-parsed object from storage must still be able to offer it back to
 * the user as text for the unsupported-newer-schema case.
 * @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}}
 */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    // Number.isInteger(NaN) is false, so this also rejects a corrupted NaN
    // version - without this, `NaN > SCHEMA_VERSION` and `MIGRATIONS[NaN]`
    // are both silently false/undefined, so a corrupted version would fall
    // through as if it were already current instead of being flagged.
    return { status: 'malformed', state: null, raw: rawString };
  }

  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return {
    status: migrated ? 'migrated' : 'ok',
    state: sanitizeState(migratedState),
    raw: rawString,
  };
}

/** null when the state (already sanitized) would exceed the storage budget - the
 * caller should keep whatever was previously saved rather than write this. */
export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function addMember(state, name) {
  if (state.members.length >= LIMITS.maxMembers) return state;
  const member = { id: makeMemberId(), name: clampSingleLineText(name, LIMITS.memberName) };
  return { ...state, members: [...state.members, member] };
}

/** Dedicated setter (rather than routing through a generic dot-path helper)
 * because `members` is an array - a generic {...cursor} shallow-clone used for
 * every other nested field would silently turn it into a plain object keyed
 * "0","1","2" instead of preserving array-ness. */
export function renameMember(state, memberId, name) {
  return {
    ...state,
    members: state.members.map((m) => (m.id === memberId ? { ...m, name: clampSingleLineText(name, LIMITS.memberName) } : m)),
  };
}

/** Cascades through role assignments and learning goals - see sanitizeState's
 * doc comment for why dangling references are also caught there as a backstop. */
export function removeMember(state, memberId) {
  const members = state.members.filter((m) => m.id !== memberId);
  const memberGoals = { ...state.goals.memberGoals };
  delete memberGoals[memberId];
  const clearIfRemoved = (id) => (id === memberId ? null : id);

  return {
    ...state,
    members,
    goals: { ...state.goals, memberGoals },
    roles: {
      ...state.roles,
      coordinator: clearIfRemoved(state.roles.coordinator),
      integrator: clearIfRemoved(state.roles.integrator),
      qualityBar: clearIfRemoved(state.roles.qualityBar),
      liaison: clearIfRemoved(state.roles.liaison),
    },
  };
}

export function countCompletedSections(state) {
  let count = 5; // meetings/comms/quality/decisions/accountability always carry a preset
  if (state.team.name) count++;
  if (state.goals.ambition) count++;
  if (state.roles.coordinator || state.roles.integrator || state.roles.qualityBar || state.roles.liaison) count++;
  if (state.commitment.statement || state.commitment.revisitDate) count++;
  return count;
}

function p(text) {
  return { type: 'p', text };
}

function list(items) {
  return { type: 'list', items };
}

/**
 * The single structured representation of a charter that both the live DOM
 * preview and buildMarkdown() render from - text only (no HTML), so the
 * DOM-side renderer can safely use textContent for every field here.
 */
export function getDocumentModel(state) {
  const memberName = (id) => state.members.find((m) => m.id === id)?.name || null;
  const sections = [];

  // Team name/course/term/members are exposed at the top level below, not as
  // a `sections` entry - both renderers (the DOM preview's title/subtitle/
  // member list, and buildMarkdown's header + Team heading) read them
  // directly from there, so there is exactly one place each can drift from.

  const goalsBlocks = [p(state.goals.ambition ? AMBITION_TEXT[state.goals.ambition] : '[Choose a grade ambition]')];
  for (const m of state.members) {
    const goal = state.goals.memberGoals[m.id];
    goalsBlocks.push(p(`${m.name || '[Unnamed member]'}: ${goal || '[add a learning goal]'}`));
  }
  sections.push({ key: 'goals', title: 'Goals & Ambition', blocks: goalsBlocks });

  const roleLine = (label, id) => `${label}: ${memberName(id) || '[unassigned]'}`;
  sections.push({
    key: 'roles',
    title: 'Roles & Ownership',
    blocks: [
      list([
        roleLine('Coordinator', state.roles.coordinator),
        roleLine('Integrator', state.roles.integrator),
        roleLine('Quality bar', state.roles.qualityBar),
        roleLine('Liaison', state.roles.liaison),
      ]),
      p(ROTATION_TEXT[state.roles.rotation]),
    ],
  });

  const meetingNorms = [];
  if (state.meetings.norms.agenda24h) meetingNorms.push('Agenda posted 24 hours before or the meeting is cancelled.');
  if (state.meetings.norms.hardStop) meetingNorms.push('Meetings start on time with a hard stop.');
  if (state.meetings.norms.graceFive) meetingNorms.push('5-minute grace period, then we start without you.');
  const meetingsBlocks = [p(CADENCE_TEXT[state.meetings.cadence]), list(meetingNorms)];
  if (state.meetings.customNote) meetingsBlocks.push(p(state.meetings.customNote));
  sections.push({ key: 'meetings', title: 'Meetings', blocks: meetingsBlocks });

  const commsLines = [CHANNEL_TEXT[state.comms.channel]];
  if (state.comms.noDms) commsLines.push("Decisions don't live in DMs - anything decided gets posted where the whole team can see it.");
  commsLines.push(SLA_TEXT[state.comms.sla]);
  if (state.comms.quietHours) commsLines.push(`Quiet hours: ${state.comms.quietHours}.`);
  if (state.comms.recruitingNote) commsLines.push("We'll flag recruiting-season conflicts as early as possible rather than going dark.");
  commsLines.push(WORKLOAD_SIGNAL_TEXT[state.comms.workloadSignal]);
  const commsBlocks = [list(commsLines)];
  if (state.comms.customNote) commsBlocks.push(p(state.comms.customNote));
  sections.push({ key: 'comms', title: 'Communication', blocks: commsBlocks });

  const doneMeans = [];
  if (state.quality.doneMeans.proofread) doneMeans.push('Proofread by someone other than the author.');
  if (state.quality.doneMeans.rubric) doneMeans.push('Matches the assignment rubric line-by-line.');
  if (state.quality.doneMeans.earlySubmit) doneMeans.push('Submitted at least 2 hours before the deadline, not at the wire.');
  const qualityBlocks = [p('"Done" means:'), list(doneMeans), p(BUFFER_TEXT[state.quality.buffer])];
  if (state.quality.customNote) qualityBlocks.push(p(state.quality.customNote));
  sections.push({ key: 'quality', title: 'Quality Bar & Deadlines', blocks: qualityBlocks });

  const decisionsBlocks = [
    p(DECISION_METHOD_TEXT[state.decisions.method]),
    p(`Needs the whole team: ${state.decisions.wholeTeamItems || '[list what always needs everyone]'}`),
  ];
  if (state.decisions.customNote) decisionsBlocks.push(p(state.decisions.customNote));
  sections.push({ key: 'decisions', title: 'Decisions', blocks: decisionsBlocks });

  const escalation = [
    'Raise it directly with the person, privately, as soon as it happens.',
    "If that doesn't resolve it, raise it with the whole team at the next meeting.",
    `If it's still unresolved, bring in ${memberName(state.roles.coordinator) || 'the Coordinator'} to help mediate.`,
    'Faculty involvement is reserved for honor-code territory only.',
  ];
  const accountabilityBlocks = [list(escalation)];
  if (state.accountability.threeStrikes) {
    accountabilityBlocks.push(p("If someone misses three commitments without a heads-up, the team has an honest conversation about whether they're still able to contribute."));
  }
  accountabilityBlocks.push(p(RETRO_TEXT[state.accountability.retroCadence]));
  if (state.accountability.peerEvalNote) {
    accountabilityBlocks.push(p("Peer evaluations contain no surprises - if something's wrong, we say it here first, not just in the eval."));
  }
  if (state.accountability.repairCommitment) {
    accountabilityBlocks.push(p("Once something is resolved, we treat it as closed - we don't bring up old conflicts in unrelated situations."));
  }
  if (state.accountability.customNote) accountabilityBlocks.push(p(state.accountability.customNote));
  sections.push({ key: 'accountability', title: 'Conflict & Accountability', blocks: accountabilityBlocks });

  const commitmentStatement = state.commitment.statement ||
    "By signing below, we commit to the agreements in this charter and to raising it - not abandoning it - if something isn't working.";

  return {
    team: state.team,
    members: state.members,
    sections,
    commitment: {
      statement: commitmentStatement,
      revisitDate: state.commitment.revisitDate,
      signatures: state.commitment.signaturesEnabled ? state.members.map((m) => m.name || '[Unnamed member]') : [],
    },
  };
}

/** Escapes markdown-structural characters so untrusted text can never reshape
 * the document or smuggle raw HTML through a markdown-to-HTML render. */
export function escapeMarkdown(text) {
  const escaped = String(text)
    .replace(/\\/g, '\\\\')
    .replace(/([`*_{}[\]()#+!|<>~&])/g, '\\$1');
  return escaped
    // Up to 3 leading spaces are allowed before a block marker in CommonMark,
    // so anchoring these at column 0 alone let an *indented* bullet, setext
    // underline, or thematic break through as live structure - very ordinary
    // in pasted text. (4+ spaces is an indented code block instead, a
    // different and harmless transformation, so it's deliberately left alone.)
    .replace(/^( {0,3})([-=>])/gm, '$1\\$2')
    // Ordered-list markers ("1. " or "1) ") are the one line-leading
    // structural form the two rules above don't already neutralize - ")" is
    // covered by the character-class escape above, "." never is.
    .replace(/^( {0,3})(\d{1,9})\./gm, '$1$2\\.');
}

export function buildMarkdown(state) {
  const doc = getDocumentModel(state);
  const lines = [];

  lines.push(`# ${escapeMarkdown(doc.team.name) || 'Team Charter'}`);
  const courseTerm = [doc.team.course, doc.team.term].filter(Boolean).map(escapeMarkdown).join(' — ');
  if (courseTerm) lines.push(`*${courseTerm}*`);
  lines.push('');

  // Mirrors renderDocument()'s #charter-doc-members - both read the member
  // list directly off doc.members, not off a `sections` entry.
  lines.push('## Team');
  lines.push('');
  const memberNames = doc.members.length ? doc.members.map((m) => m.name || '[Unnamed member]') : ['[Add team members]'];
  for (const name of memberNames) lines.push(`- ${escapeMarkdown(name)}`);
  lines.push('');

  for (const section of doc.sections) {
    lines.push(`## ${section.title}`);
    lines.push('');
    for (const block of section.blocks) {
      if (block.type === 'p') {
        lines.push(escapeMarkdown(block.text));
        lines.push('');
      } else if (block.type === 'list') {
        for (const item of block.items) lines.push(`- ${escapeMarkdown(item)}`);
        lines.push('');
      }
    }
  }

  lines.push('## Commitment');
  lines.push('');
  lines.push(escapeMarkdown(doc.commitment.statement));
  lines.push('');
  if (doc.commitment.revisitDate) {
    lines.push(`Revisit on: ${escapeMarkdown(doc.commitment.revisitDate)}`);
    lines.push('');
  }
  if (doc.commitment.signatures.length) {
    lines.push('Signatures:');
    for (const name of doc.commitment.signatures) lines.push(`- ${escapeMarkdown(name)}: ______________________`);
    lines.push('');
  }

  lines.push('---');
  lines.push('*Built with the free MBA Lab team charter builder - mbalab.net/tools/team-charter-builder*');

  return lines.join('\n');
}
